import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { Input } from "components/form_components/Inputs";
import { createUseDebounce } from "lib/debounce";
import { INLINE_FILTERS, useVariableValue } from "lib/featureFlags";
import { SavedViewType } from "lib/generated/app-api";
import { useUpdateView } from "../../../lib/hooks/api/notices/views/useUpdateView";
import { useFormAutoSubmit } from "../../../lib/hooks/useFormAutoSubmit";
import { useMyFeedPageState } from "../useMyFeedPageState";
import ViewOptions from "./ViewOptions";

import css from "./ViewHeaderText.module.scss";

type FormValues = {
  viewName: string;
  viewDescription: string;
};

type Props = {
  view: SavedViewType;
  pinnedViews: SavedViewType[];
};

export function ViewHeaderText({ view, pinnedViews }: Props) {
  const [viewId, setViewId] = useState<string>();
  const [titleDisplay, setTitleDisplay] = useState<string>(view.name);
  const [descriptionDisplay, setDescriptionDisplay] = useState<string>(view.description ?? "");
  const { hasUserModifiedView } = useMyFeedPageState();
  const { control, getValues, trigger, watch, reset } = useForm<FormValues>({
    defaultValues: {
      viewName: view.name,
      viewDescription: view.description ? view.description : "",
    },
    mode: "onChange",
  });

  const { mutate: mutateView } = useUpdateView("Inline title/description edit");

  const userDebounce500 = createUseDebounce(500);

  const debouncedMutateView = userDebounce500(mutateView);

  const isInlineFiltersEnabled = useVariableValue(INLINE_FILTERS, false);

  const viewStatus = hasUserModifiedView
    ? { status: "unsaved", message: "Unsaved" }
    : view.isStandardView
      ? { status: "standard", message: "Standard view" }
      : { status: "saved", message: "Saved" };

  // reset the form on change
  useEffect(() => {
    if (view.id !== viewId) {
      setTitleDisplay(view.name);
      setDescriptionDisplay(view.description ?? "");
      setViewId(view.id);
      reset({ viewName: view.name, viewDescription: view.description ?? "" });
    }
  }, [reset, view.description, view.id, view.name, viewId]);

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: (data) => {
      setTitleDisplay(data.viewName);
      setDescriptionDisplay(data.viewDescription);

      debouncedMutateView({
        view: {
          ...view,
          name: data.viewName,
          description: data.viewDescription,
        },
      });
    },
  });

  return (
    <Flex column>
      <form>
        <Flex justifyContent="space-between">
          {!isInlineFiltersEnabled && (
            <Input
              containerClassName={css.currentViewTitleContainer}
              className={css.currentViewTitle}
              name="viewName"
              label=""
              disabled={view.isStandardView}
              type="text"
              control={control}
              value={titleDisplay}
              placeholder="View name"
              rules={{
                required: {
                  value: true,
                  message: "View name is required",
                },
              }}
            />
          )}
          {isInlineFiltersEnabled && (
            <Flex alignItems="left" gap="8px">
              <ViewName>{titleDisplay}</ViewName>
              <Flex alignItems="center">
                <StandardViewIcon status={viewStatus.status as "saved" | "unsaved" | "standard"}>
                  {viewStatus.message}
                </StandardViewIcon>
              </Flex>
            </Flex>
          )}
          <ViewOptions selectedView={view} pinnedViews={pinnedViews} />
        </Flex>
        {!isInlineFiltersEnabled && (
          <Input
            className={css.currentViewDescription}
            name="viewDescription"
            label=""
            disabled={view.isStandardView}
            type="text"
            control={control}
            value={descriptionDisplay}
            placeholder="What does this view show? Enter a description here..."
          />
        )}
      </form>
    </Flex>
  );
}

export function NoViewHeaderText({ pinnedViews }: { pinnedViews: SavedViewType[] }) {
  return (
    <Flex column flexGrow={1}>
      <Flex>
        <div className={`${css.currentViewTitle} ${css.currentViewTitleContainer}`}>
          Find notices
        </div>
        <ViewOptions selectedView={undefined} pinnedViews={pinnedViews} />
      </Flex>
      <div className={css.currentViewDescription}>Search all public sector procurement notices</div>
    </Flex>
  );
}

const getStyles = (status: "saved" | "unsaved" | "standard", theme: Theme) => {
  switch (status) {
    case "saved":
      return {
        background: theme.colors.sysPrimarySubtle,
        color: theme.colors.sysTextDefault,
      };
    case "unsaved":
      return {
        background: theme.colors.sysWarningSubtle,
        color: "#7C4F07",
      };
    case "standard":
      return {
        background: theme.colors.sysBackgroundAlternative,
        color: theme.colors.sysTextSecondary,
      };
    default:
      return {};
  }
};

const ViewName = styled.h1(({ theme }) => ({
  color: theme.colors.sysTextDefault,
  fontFamily: "Inter",
  fontSize: "20px",
  fontStyle: "normal",
  fontWeight: "700",
  lineHeight: "25px",
  margin: 0,
}));

const StandardViewIcon = styled.div<{ status: "saved" | "unsaved" | "standard" }>(
  ({ theme, status }) => ({
    display: "flex",
    height: "24px",
    padding: "8px",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    borderRadius: "4px",
    fontFamily: "Inter",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "150%",
    ...getStyles(status, theme),
  }),
);
